<template>
  <div id="award-category-list" class="page-container">
    <!-- Stunning Header Card -->
    <v-card elevation="20" class="header-card mb-4 overflow-hidden">
      <div class="header-gradient">
        <div class="header-content pa-5">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="6">
              <div class="d-flex align-center">
                <div class="header-icon-box mr-4">
                  <v-icon size="48" color="white">mdi-gavel</v-icon>
                </div>
                <div>
                  <h1
                    class="text-h4 font-weight-bold white--text mb-1 header-title"
                  >
                    {{ PageTitle || "Judges List" }}
                  </h1>
                  <p class="text-subtitle-1 white--text mb-0 header-subtitle">
                    <v-icon size="16" color="white" class="mr-1"
                      >mdi-information-outline</v-icon
                    >
                    {{ PageDescription || "List of all judges" }}
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="text-right">
              <div class="d-flex justify-end flex-wrap gap-2">
                <!-- Refresh Button -->
                <v-btn
                  @click.prevent="refreshPageData"
                  color="rgba(255,255,255,0.2)"
                  elevation="0"
                  rounded
                  large
                  class="action-btn white--text"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>

                <!-- Create Button -->
                <router-link
                  to="/jci-award/judges/create"
                  style="text-decoration: none"
                >
                  <v-btn
                    color="#FF9800"
                    elevation="8"
                    rounded
                    large
                    class="action-btn white--text pulse-animation"
                  >
                    <v-icon left>mdi-account-plus</v-icon>
                    CREATE
                  </v-btn>
                </router-link>

                <!-- Upload Button -->
                <router-link
                  to="/jci-award/judges/upload"
                  style="text-decoration: none"
                >
                  <v-btn
                    color="#8950FC"
                    elevation="8"
                    rounded
                    large
                    class="action-btn white--text"
                  >
                    <v-icon left>mdi-cloud-upload</v-icon>
                    UPLOAD
                  </v-btn>
                </router-link>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Filter Section -->
    <v-card elevation="8" class="filter-card mb-4">
      <v-card-text class="pa-5">
        <!-- Loading -->
        <div v-if="LoadingFlag" class="text-center py-8">
          <v-progress-circular
            :size="70"
            :width="7"
            color="primary"
            indeterminate
          ></v-progress-circular>
          <p class="mt-4 text-h6 primary--text">Loading judges data...</p>
        </div>

        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-if="!LoadingFlag"
        >
          <!-- Filter Header -->
          <div class="filter-header mb-4">
            <v-icon color="primary" size="28" class="mr-2"
              >mdi-filter-variant</v-icon
            >
            <span class="text-h5 font-weight-bold primary--text"
              >Search & Filter</span
            >
          </div>

          <v-row>
            <v-col cols="12" md="4">
              <v-autocomplete
                :items="ZoneCodeOptions"
                :rules="ZoneCodeRules"
                v-model="ZoneCode"
                label="Zone"
                prepend-inner-icon="mdi-map-marker"
                outlined
                dense
                clearable
                required
                class="custom-input"
              >
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-map-marker-radius</v-icon>
                </template>
              </v-autocomplete>
            </v-col>
            <!-- Judge Type -->
            <v-col cols="12" md="4">
              <v-autocomplete
                :items="JudgeTypeOptions"
                :rules="JudgeTypeRules"
                label="Judge Type"
                v-model="JudgeType"
                outlined
                dense
                clearable
                class="custom-input"
                prepend-inner-icon="mdi-account-tie"
              >
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-gavel</v-icon>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="12" class="d-flex justify-center">
              <v-btn
                :disabled="!valid1"
                @click.prevent="submitForm"
                :loading="SubmitFlag"
                color="primary"
                elevation="8"
                x-large
                rounded
                class="search-btn"
              >
                <v-icon left size="24">mdi-magnify</v-icon>
                SEARCH JUDGES
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Results Section -->
    <div v-if="tableData1.length > 0">
      <!-- Stats Bar -->
      <v-card elevation="8" class="stats-card mb-4">
        <v-card-text class="pa-4">
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="d-flex align-center">
                <div class="stats-icon-box mr-3">
                  <v-icon size="32" color="white">mdi-account-group</v-icon>
                </div>
                <div>
                  <h2 class="text-h4 font-weight-bold white--text mb-0">
                    {{ tableData1.length }}
                  </h2>
                  <p class="text-subtitle-1 white--text mb-0">
                    Total Judges Found
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="text-right">
              <v-btn
                v-if="DownloadFlag"
                @click="downloadExcel"
                color="#20B2AA"
                elevation="8"
                large
                rounded
                class="export-btn white--text"
              >
                <v-icon left>mdi-file-excel</v-icon>
                EXPORT TO EXCEL
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Search Bar -->
      <v-card elevation="8" class="search-card mb-4">
        <v-card-text class="pa-4">
          <v-text-field
            v-model="search"
            placeholder="🔍 Search by name, zone, mobile, email..."
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-magnify"
            class="search-input"
          >
            <template v-slot:append>
              <v-chip v-if="search" color="success" small>
                <v-icon small left>mdi-check</v-icon>
                Searching
              </v-chip>
            </template>
          </v-text-field>
        </v-card-text>
      </v-card>

      <!-- Beautiful Data Table -->
      <v-card elevation="12" class="table-card">
        <v-data-table
          v-model="selected1"
          :search="search"
          :item-key="tableOptions1.ItemKey"
          :headers="tableColumns1"
          :items="tableData1"
          :items-per-page="tableOptions1.ItemsPerPage"
          :single-select="tableOptions1.SingleSelectFlag"
          :show-select="tableOptions1.ShowSelectFlag"
          :footer-props="tableOptions1.FooterProps"
          class="stunning-table"
        >
          <!-- Custom Headers -->
          <template v-slot:header="{ props }">
            <thead class="table-header">
              <tr>
                <th
                  v-for="header in props.headers"
                  :key="header.text"
                  class="header-cell"
                >
                  <div class="d-flex align-center">
                    <v-icon
                      v-if="getHeaderIcon(header.text)"
                      small
                      class="mr-2"
                      >{{ getHeaderIcon(header.text) }}</v-icon
                    >
                    <span class="font-weight-bold">{{ header.text }}</span>
                  </div>
                </th>
              </tr>
            </thead>
          </template>

          <!-- Status Column -->
          <template v-slot:item.ActiveStatusTxt="{ item }">
            <v-chip
              :color="getActiveStatusColor(item.ActiveStatusTxt)"
              dark
              small
              class="font-weight-bold status-chip"
            >
              <v-icon small left>mdi-circle-medium</v-icon>
              {{ item.ActiveStatusTxt }}
            </v-chip>
          </template>

          <!-- Contact Column -->
          <template v-slot:item.ContactTxt="{ item }">
            <div v-html="item.ContactTxt" class="contact-cell"></div>
          </template>

          <!-- Image Column -->
          <template v-slot:item.MemberImagePath="{ item }">
            <v-avatar size="60" class="judge-avatar">
              <img :src="item.MemberImagePath" alt="Judge" />
            </v-avatar>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.Actions="{ item }">
            <div class="action-buttons">
              <!-- View Button -->
              <v-tooltip bottom v-if="item.PreviewFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    @click="previewAlert(item, e)"
                    v-bind="attrs"
                    v-on="on"
                    class="action-icon-btn"
                  >
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>View Details</span>
              </v-tooltip>

              <!-- Edit Button -->
              <v-tooltip bottom v-if="item.EditButtonFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="warning"
                    @click="editAlert(item, e)"
                    v-bind="attrs"
                    v-on="on"
                    class="action-icon-btn"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Reset Active Status</span>
              </v-tooltip>

              <!-- Delete Button -->
              <v-tooltip bottom v-if="item.DeleteButtonFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="error"
                    @click="deleteAlert(item, e)"
                    v-bind="attrs"
                    v-on="on"
                    class="action-icon-btn"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

export default {
  mixins: [common],
  components: {},
  data() {
    return {
      valid1: true,
      search: "",
      ProgessStart: 0,
      uploadPercentage: 0,

      LoadingFlag: false,
      SubmitFlag: false,
      SelectFlag: true,
      DeleteFlag: false,

      alert: {},
      valid: true,
      RoleId: "",
      ResultFlag: 0,

      ZoneCodeRules: [(v) => !!v || "Zone is required"],
      ZoneCode: "",
      ZoneCodeOptions: [],

      JudgeType: "",
      JudgeTypeOptions: [
        {
          value: "",
          text: "--Select Judge Type--",
        },
        {
          value: 174,
          text: "Zone Head Table",
        },
        {
          value: 175,
          text: "National Head Table",
        },
        {
          value: 196,
          text: "EO",
        },
        {
          value: 173,
          text: "Judge",
        },
        {
          value: 310,
          text: "Nomination Committee Chairman",
        },
        {
          value: 311,
          text: "Nomination Committee Member",
        },
        {
          value: 312,
          text: "OTS Chairman",
        },
        {
          value: 313,
          text: "PA Chairman",
        },
      ],

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      recordData: {},

      DownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: {},

      dialog: false,
      dialogDelete: false,
      rows: [],
      tableData1: [],
      tableOptions1: [],
      tableColumns1: [],
      TotalMembers: 0,
      selected1: [],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.getZoneCodeOptions();
      }
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.ZoneCodeOptionsLoading = false;
    },
    ZoneCode: function () {
      console.log("ZoneCode called");
      this.resetForm();
    },
  },
  created() {},
  methods: {
    getHeaderIcon(headerText) {
      const iconMap = {
        Status: "mdi-check-circle",
        "Organization Type": "mdi-office-building",
        "Judge Type": "mdi-gavel",
        "Judge Name": "mdi-account",
        Contact: "mdi-phone",
        UserName: "mdi-account-circle",
        Password: "mdi-lock",
        Actions: "mdi-cog",
      };
      return iconMap[headerText] || null;
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "award_judges",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    resetForm() {
      console.log("resetForm called");
      this.tableData1 = [];
      this.tableOptions1 = [];
      this.tableColumns1 = [];
    },
    downloadExcel() {
      console.log("downloadExcel called");
      try {
        const excelFields = this.tableOptions1.ExcelFields;
        const exportData = this.tableData1.map((item) => {
          const row = {};
          Object.keys(excelFields).forEach((header) => {
            const fieldName = excelFields[header];
            row[header] = item[fieldName];
          });
          return row;
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Judges");
        XLSX.writeFile(wb, this.ExcelFileName);

        this.toast("success", "Excel file exported successfully");
      } catch (err) {
        console.error("Error downloading file:", err);
        this.toast("error", "Failed to export file");
      }
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var selectbox1_source = "ZoneCode";
      var selectbox1_destination = "ZoneCodeOptions";
      var selectbox1_url = "api/zone/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        NotAvailable: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    submitForm() {
      console.log("submitForm called");

      var result = this.$refs.form1.validate();
      console.log("result=" + result);

      if (result) {
        this.SubmitFlag = true;
        this.DownloadFlag = false;
        this.ExcelFileName = "";
        this.ZipDownloadFlag = false;
        this.ZipFileName = "";
        this.tableData1 = [];
        this.selected1 = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judge/lists";
        var upload = {
          UserInterface: 1,
          Zone: this.ZoneCode,
          JudgeType: this.JudgeType,
        };
        console.log("upload=" + JSON.stringify(upload));

        const thisIns = this;
        var output = "";
        var records = [];
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));
            thisIns.SubmitFlag = false;
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;

            var options = records.TableOptions;
            thisIns.tableOptions1 = options;

            if (flag == 1) {
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableData1 = records.TableData;

              thisIns.DownloadFlag = options.DownloadFlag;
              thisIns.ExcelFileName = options.ExcelFileName;
              thisIns.ExcelFields = options.ExcelFields;

              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Kindly fill required fields and then submit";
        this.toast("error", message);
      }
    },
    editAlert(tr) {
      console.log("editAlert called");
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        this.selectedData = tr;
        this.confirmAlert(1);
      } else {
        this.sweetAlert("error", "Kindly select one record to delete", false);
      }
    },
    deleteAlert(tr) {
      console.log("deleteAlert called");
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        this.selectedData = tr;
        this.confirmAlert(2);
      } else {
        this.sweetAlert("error", "Kindly select one record to delete", false);
      }
    },
    confirmAlert(id) {
      var tr = this.selectedData;
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt +=
          "<br/>Organization Type <b> " + tr.OrganizationTypeName + "</b>";
        htmlTxt += "<br/>Judge Name <b> " + tr.MemberFullNameTxt + "</b>";
        htmlTxt += "<br/>Judge MobileNo <b> " + tr.MobileNo + "</b>";
        htmlTxt += "<br/>Judge EmailId <b> " + tr.EmailId + "</b>";
        htmlTxt += "<br/><br/>Do you want to delete the data?";

        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to delete the data?",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: id == 1 ? `Reset Active Status` : `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          if (result.isConfirmed) {
            if (id == 1) {
              this.ResetJudgeStatus(tr);
            } else {
              this.deleteRecord(tr);
            }
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      }
    },
    ResetJudgeStatus(tr) {
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judge/reset-active-status";
        var upload = {
          UserInterface: 1,
          Judge: tr.JudgeId,
          User: tr.UserId,
        };

        const thisIns = this;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            thisIns.LoadingFlag = false;

            var output = response.data.output;
            var flag = response.data.flag;

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.submitForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            thisIns.LoadingFlag = false;
          });
      }
    },
    deleteRecord(tr) {
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judge/destroy";
        var upload = {
          UserInterface: 1,
          Judge: tr.JudgeId,
          JudgeName: tr.JudgeName,
          User: tr.UserId,
          Role: tr.RoleId,
          Zone: tr.ZoneId,
        };

        const thisIns = this;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            thisIns.LoadingFlag = false;

            var output = response.data.output;
            var flag = response.data.flag;

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.submitForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            thisIns.LoadingFlag = false;
          });
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
.page-container {
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

/* ============================================
   HEADER CARD
   ============================================ */
.header-card {
  border-radius: 20px !important;
  overflow: hidden;
  animation: slideDown 0.6s ease-out;
}

.header-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    animation: rotate 20s linear infinite;
  }
}

.header-content {
  position: relative;
  z-index: 1;
}

.header-icon-box {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 15px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  animation: float 3s ease-in-out infinite;
}

.header-title {
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  animation: fadeInUp 0.8s ease-out;
}

.header-subtitle {
  opacity: 0.95;
  animation: fadeInUp 1s ease-out;
}

.action-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 700;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
  }
}

.pulse-animation {
  animation: pulse 2s infinite;
}

/* ============================================
   FILTER CARD
   ============================================ */
.filter-card {
  border-radius: 16px !important;
  animation: fadeIn 0.8s ease-out;
}

.filter-header {
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 3px solid #667eea;
  margin-bottom: 24px;
}

.custom-input {
  ::v-deep .v-input__slot {
    border-radius: 12px !important;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
    }
  }
}

.search-btn {
  font-weight: 700;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4) !important;
  }
}

/* ============================================
   STATS CARD
   ============================================ */
.stats-card {
  border-radius: 16px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  animation: slideInLeft 0.8s ease-out;
}

.stats-icon-box {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.export-btn {
  font-weight: 700;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(32, 178, 170, 0.4) !important;
  }
}

/* ============================================
   SEARCH CARD
   ============================================ */
.search-card {
  border-radius: 16px !important;
  animation: fadeIn 1s ease-out;
}

.search-input {
  ::v-deep .v-input__slot {
    border-radius: 50px !important;
    border: 2px solid #667eea !important;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
    }
  }
}

/* ============================================
   TABLE CARD
   ============================================ */
.table-card {
  border-radius: 16px !important;
  overflow: hidden;
  animation: fadeInUp 1s ease-out;
}

.stunning-table {
  ::v-deep {
    .table-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

      .header-cell {
        color: white !important;
        font-weight: 700 !important;
        font-size: 14px !important;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 20px 16px !important;
        border: none !important;
      }
    }

    tbody tr {
      transition: all 0.3s ease;

      &:hover {
        background: linear-gradient(
          90deg,
          rgba(102, 126, 234, 0.05) 0%,
          rgba(118, 75, 162, 0.05) 100%
        );
        transform: scale(1.01);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      td {
        padding: 16px !important;
        border-bottom: 1px solid #e0e0e0 !important;
      }
    }
  }
}

.status-chip {
  font-weight: 700;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }
}

.judge-avatar {
  border: 3px solid #667eea;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-icon-btn {
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.2) rotate(10deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

.contact-cell {
  line-height: 1.6;
}

/* ============================================
   ANIMATIONS
   ============================================ */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 960px) {
  .header-content {
    text-align: center;

    .d-flex {
      flex-direction: column;
      align-items: center !important;
    }

    .text-right {
      text-align: center !important;
    }
  }

  .action-btn {
    margin: 4px 0 !important;
  }
}
</style>
